import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./footer";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDateForInput = (value) => {
  if (!value) return "";

  const dateText = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(dateText)) {
    return dateText.slice(0, 10);
  }

  const dateParts = dateText.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dateParts) {
    const [, day, month, year] = dateParts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return "";
};

const formatDateForRequest = (value) => value;

const toNumber = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  return Number(value);
};

const LiveFilteredStocks = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = window.sessionStorage.getItem("jwtToken");
  const strategyCode = location.state?.strategyCode || "";
  const businessDate = location.state?.businessDate || "";
  console.log(businessDate);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [openSection, setOpenSection] = useState("live");
  const [errorPopup, setErrorPopup] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const pageSize = 20;

  const [livePage, setLivePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);

  const [addFormData, setAddFormData] = useState({
    strategyCode: strategyCode,
    strategyName: "",
    stockName: "",
    businessDate: businessDate,
    entryDate: "",
    entryPrice: "",
    exitDate: "",
    exitPrice: "",
    quantity: "",
    weightage: "",
    status: 1,
    currentPrice: "",
    pnl: "",
    averageEntryPrice: "",
  });

  useEffect(() => {
    if (!strategyCode) {
      toast.error("Strategy Code not found");
      return;
    }

    fetchStocks();
  }, [strategyCode]);

  const fetchStocks = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${BASE_URL}/getStocks-filtered/${strategyCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      console.log("API Response:", result);

      if (result.data) {
        setStocks(result.data);
      } else {
        setStocks([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load stocks");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setStocks((prevStocks) =>
      prevStocks.map((stock) =>
        stock.id === id ? { ...stock, [field]: value } : stock,
      ),
    );
  };

  const buildStockPayload = (stock) => ({
    ...stock,
    entryDate: formatDateForRequest(stock.entryDate),
    exitDate: formatDateForRequest(stock.exitDate),
    entryPrice: toNumber(stock.entryPrice),
    exitPrice: toNumber(stock.exitPrice),
    quantity: toNumber(stock.quantity),
    weightage: toNumber(stock.weightage),
    status: toNumber(stock.status, 1),
    currentPrice: toNumber(stock.currentPrice),
    pnl: toNumber(stock.pnl),
    averageEntryPrice: toNumber(stock.averageEntryPrice),
  });

  const handleUpdateStock = async (stock) => {
    // take values from editable state ONLY
    const entry = formatDateForInput(stock.entryDate);
    const exit = formatDateForInput(stock.exitDate);

    // reset previous error
    setErrorPopup("");

    if (entry && exit && new Date(exit) < new Date(entry)) {
      setErrorPopup("Exit Date cannot be before Entry Date");
      return;
    }

    const payload = buildStockPayload(stock);

    try {
      const response = await fetch(
        `${BASE_URL}/editStocks-filtered/${stock.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message);
        setEditingId(null);
        fetchStocks();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Failed to update stock");
    }
  };

  const handleAddStock = async () => {
    const errors = {};

    const {
      stockName,
      entryDate,
      entryPrice,
      exitDate,
      exitPrice,
      quantity,
      weightage,
      currentPrice,
      pnl,
      averageEntryPrice,
    } = addFormData;

    if (!stockName?.trim()) errors.stockName = "Stock Name is required";
    if (!entryPrice) errors.entryPrice = "Entry Price is required";
    if (!exitPrice) errors.exitPrice = "Exit Price is required";
    if (!quantity) errors.quantity = "Quantity is required";
    if (!weightage) errors.weightage = "Weightage is required";
    if (!currentPrice) errors.currentPrice = "Current Price is required";
    if (!pnl) errors.pnl = "PnL is required";
    if (!averageEntryPrice)
      errors.averageEntryPrice = "Avg Entry Price is required";

    const entry = formatDateForInput(entryDate);
    const exit = formatDateForInput(exitDate);

    if (entry && exit && new Date(exit) < new Date(entry)) {
      errors.exitDate = "Exit Date cannot be before Entry Date";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({}); // clear old errors

    try {
      const payload = {
        strategyCode,
        stockName: stockName.trim(),
        entryDate: formatDateForRequest(entryDate),
        entryPrice: toNumber(entryPrice),
        exitDate: formatDateForRequest(exitDate),
        exitPrice: toNumber(exitPrice),
        quantity: toNumber(quantity),
        weightage: toNumber(weightage),
        status: toNumber(addFormData.status, 1),
        currentPrice: toNumber(currentPrice),
        pnl: toNumber(pnl),
        averageEntryPrice: toNumber(averageEntryPrice),
      };

      const response = await fetch(`${BASE_URL}/addStocks-filtered`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setFormErrors({});
        fetchStocks();
      } else {
        setErrorPopup(result.message);
      }
    } catch (error) {
      setErrorPopup("Failed to add stock");
    }
  };

  const liveHoldings = stocks
    .filter((stock) => editingId === stock.id || Number(stock.status) === 1)
    .sort((a, b) => {
      const dateA = new Date(formatDateForInput(a.entryDate) || "1900-01-01");
      const dateB = new Date(formatDateForInput(b.entryDate) || "1900-01-01");
      return dateB - dateA; // Entry Date DESC
    });

  const pastExitStocks = stocks
    .filter((stock) => editingId !== stock.id && Number(stock.status) === 0)
    .sort((a, b) => {
      const dateA = new Date(formatDateForInput(a.exitDate) || "1900-01-01");
      const dateB = new Date(formatDateForInput(b.exitDate) || "1900-01-01");
      return dateB - dateA; // Exit Date DESC
    });

  // pagination logic
  const liveStart = (livePage - 1) * pageSize;
  const pastStart = (pastPage - 1) * pageSize;

  const paginatedLive = liveHoldings.slice(liveStart, liveStart + pageSize);
  const paginatedPast = pastExitStocks.slice(pastStart, pastStart + pageSize);

  const isValidDate = (dateString) => {
    if (!dateString) return false;

    const parts = dateString.split("-");
    if (parts.length !== 3) return false;

    const [year, month, day] = parts.map(Number);

    const date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const validateDates = (entryDate, exitDate, isEdit = false) => {
    const entry = formatDateForInput(entryDate);
    const exit = formatDateForInput(exitDate);

    let errors = {
      entryDate: "",
      exitDate: "",
    };

    // Exit date required if exit price entered

    if (exit && new Date(exit) < new Date(entry)) {
      errors.exitDate = "Exit Date cannot be before Entry Date";
    }

    if (errors.entryDate || errors.exitDate) {
      if (isEdit) {
        toast.error(errors.entryDate || errors.exitDate);
      } else {
        setFormErrors(errors);
      }
      return false;
    }

    return true;
  };

  return (
    <>
      <style>
        {`
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          input[type="number"] {
            appearance: textfield;
            -moz-appearance: textfield;
          }
          `}
      </style>
      <Navbar />

      <div className="main-content">
        <div className="container-fluid">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginTop: "10px", marginBottom: "10px" }}
          >
            <button
              className="btn btn-primary"
              onClick={() => navigate("/stocks")}
            >
              Back
            </button>

            <h4>Rebalance Stocks - {strategyCode}</h4>

            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                + Add Stock
              </button>
            </div>
          </div>

          <div className="accordion" id="stocksAccordion">
            {/* Live Holdings */}
            <div className="card mb-3">
              <div className="card-header">
                <button
                  className="btn text-decoration-none fw-bold"
                  type="button"
                  onClick={() =>
                    setOpenSection(openSection === "live" ? "" : "live")
                  }
                >
                  Live Holdings {">"}
                </button>
              </div>

              {openSection === "live" && (
                <div className="card-body">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Stock Name</th>
                        <th>Strategy Code</th>
                        <th>Entry Date</th>
                        <th>Entry Price</th>
                        <th>Exit Date</th>
                        <th>Exit Price</th>
                        <th>Quantity</th>
                        <th>Weightage</th>
                        <th>Current Price</th>
                        <th>PnL</th>
                        <th>Avg Entry Price</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedLive.map((stock) => (
                        <tr key={stock.id}>
                          <td>{stock.id}</td>

                          {/* Not Editable */}
                          <td>{stock.stockName}</td>
                          <td>{stock.strategyCode}</td>

                          {/* Editable */}
                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={formatDateForInput(stock.entryDate)}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "entryDate",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.entryDate
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.entryPrice || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "entryPrice",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.entryPrice
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                min={formatDateForInput(stock.entryDate)}
                                value={formatDateForInput(stock.exitDate)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleFieldChange(
                                    stock.id,
                                    "exitDate",
                                    value,
                                  );
                                }}
                              />
                            ) : (
                              stock.exitDate
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.exitPrice || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "exitPrice",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.exitPrice
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.quantity || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.quantity
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.weightage || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "weightage",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.weightage
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.currentPrice || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "currentPrice",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.currentPrice
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.pnl || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "pnl",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.pnl
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.averageEntryPrice || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "averageEntryPrice",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.averageEntryPrice
                            )}
                          </td>
                          <td>
                            {editingId === stock.id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={stock.status || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    stock.id,
                                    "status",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              stock.status
                            )}
                          </td>

                          <td>
                            {editingId === stock.id ? (
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleUpdateStock(stock)}
                              >
                                Save
                              </button>
                            ) : (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => setEditingId(stock.id)}
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={livePage === 1}
                      onClick={() => setLivePage((p) => p - 1)}
                    >
                      Prev
                    </button>

                    <span>
                      Page {livePage} /{" "}
                      {Math.ceil(liveHoldings.length / pageSize) || 1}
                    </span>

                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={
                        livePage >= Math.ceil(liveHoldings.length / pageSize)
                      }
                      onClick={() => setLivePage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Past Exit Stocks */}
            <div className="card">
              <div className="card-header">
                <button
                  className="btn text-decoration-none fw-bold"
                  type="button"
                  onClick={() =>
                    setOpenSection(openSection === "past" ? "" : "past")
                  }
                >
                  Past Exit Stocks {">"}
                </button>
              </div>

              {openSection === "past" && (
                <div className="card-body">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Stock Name</th>
                        <th>Strategy Code</th>
                        <th>Entry Date</th>
                        <th>Entry Price</th>
                        <th>Exit Date</th>
                        <th>Exit Price</th>
                        <th>Quantity</th>
                        <th>Weightage</th>
                        <th>Current Price</th>
                        <th>PnL</th>
                        <th>Avg Entry Price</th>
                        <th>Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedPast.map((stock) => (
                        <tr key={stock.id}>
                          <td>{stock.id}</td>
                          <td>{stock.stockName}</td>
                          <td>{stock.strategyCode}</td>
                          <td>{stock.entryDate}</td>
                          <td>{stock.entryPrice}</td>
                          <td>{stock.exitDate}</td>
                          <td>{stock.exitPrice}</td>
                          <td>{stock.quantity}</td>
                          <td>{stock.weightage}</td>
                          <td>{stock.currentPrice}</td>
                          <td>{stock.pnl}</td>
                          <td>{stock.averageEntryPrice}</td>
                          <td>{stock.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={pastPage === 1}
                      onClick={() => setPastPage((p) => p - 1)}
                    >
                      Prev
                    </button>

                    <span>
                      Page {pastPage} /{" "}
                      {Math.ceil(pastExitStocks.length / pageSize) || 1}
                    </span>

                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={
                        pastPage >= Math.ceil(pastExitStocks.length / pageSize)
                      }
                      onClick={() => setPastPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showAddModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Add Stock</h5>

                  <button
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <label>Stock Name</label>
                  <input
                    className="form-control mb-1"
                    value={addFormData.stockName}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        stockName: e.target.value,
                      })
                    }
                  />

                  {formErrors.stockName && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.stockName}
                    </div>
                  )}

                  <label>Entry Date</label>

                  <input
                    type="date"
                    className="form-control"
                    value={formatDateForInput(addFormData.entryDate)}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        entryDate: e.target.value,
                      });

                      setFormErrors((prev) => ({
                        ...prev,
                        entryDate: "",
                      }));
                    }}
                  />

                  {formErrors.entryDate && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      {formErrors.entryDate}
                    </div>
                  )}

                  <label>Entry Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.entryPrice}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        entryPrice: e.target.value,
                      })
                    }
                  />
                  {formErrors.entryPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.entryPrice}
                    </div>
                  )}

                  <label>Exit Date</label>
                  <input
                    type="date"
                    className="form-control"
                    min={formatDateForInput(addFormData.entryDate)}
                    value={formatDateForInput(addFormData.exitDate)}
                    onChange={(e) => {
                      setAddFormData((prev) => ({
                        ...prev,
                        exitDate: e.target.value,
                      }));

                      setFormErrors((prev) => ({
                        ...prev,
                        exitDate: "",
                      }));
                    }}
                  />

                  {formErrors.exitDate && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "12px",
                      }}
                    >
                      {formErrors.exitDate}
                    </div>
                  )}
                  <label>Exit Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.exitPrice}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        exitPrice: e.target.value,
                      })
                    }
                  />
                  {formErrors.exitPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.exitPrice}
                    </div>
                  )}

                  <label>Quantity</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.quantity}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        quantity: e.target.value,
                      })
                    }
                  />
                  {formErrors.quantity && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.quantity}
                    </div>
                  )}

                  <label>Weightage</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.weightage}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        weightage: e.target.value,
                      })
                    }
                  />
                  {formErrors.weightage && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.weightage}
                    </div>
                  )}
                  <label>Status</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.status}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        status: e.target.value,
                      })
                    }
                  />

                  <label>Current Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.currentPrice}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        currentPrice: e.target.value,
                      })
                    }
                  />
                  {formErrors.currentPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.currentPrice}
                    </div>
                  )}

                  <label>PnL</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.pnl}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        pnl: e.target.value,
                      })
                    }
                  />
                  {formErrors.pnl && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.pnl}
                    </div>
                  )}

                  <label>Avg Entry Price</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={addFormData.averageEntryPrice}
                    onChange={(e) =>
                      setAddFormData({
                        ...addFormData,
                        averageEntryPrice: e.target.value,
                      })
                    }
                  />
                  {formErrors.averageEntryPrice && (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formErrors.averageEntryPrice}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-success" onClick={handleAddStock}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {errorPopup && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Validation Error</h5>
                </div>

                <div className="modal-body">{errorPopup}</div>

                <div className="modal-footer">
                  <button
                    className="btn btn-danger"
                    onClick={() => setErrorPopup("")}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
      <Footer />
    </>
  );
};

export default LiveFilteredStocks;
